import { estadoDelPrestamo, siguienteCorte } from './prestamo';

// El mensaje que se le manda al cliente por WhatsApp cuando le cierra el mes.
//
// Se arma con las palabras del prestamista —«Buenas, Sr. Pérez» al abrir y
// «Cualquier duda me escribe» al cerrar— porque el cliente tiene que reconocer
// a quien le escribe, no leer algo que suena a banco.
//
// Nada de columnas alineadas con espacios: WhatsApp usa letra proporcional y
// se descuadra en el teléfono. Etiqueta y valor por renglón, y *negrita* solo
// en tres sitios, que si se abusa deja de resaltar.

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun',
               'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// «5 ago 2026». Con el año siempre: un estado de cuenta se guarda y se vuelve a
// mirar meses después, y «5 ago» a secas no dice de cuál año se está hablando.
export function fechaLarga(f) {
  const d = f instanceof Date ? f : new Date(String(f).length <= 10 ? f + 'T00:00:00Z' : f);
  if (isNaN(d.getTime())) return '';
  return `${d.getUTCDate()} ${MESES[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

const plata = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

// Los nombres se anotan en mayúscula sostenida —así vienen de la cédula— y un
// «Buenas, ACOSTA CANTILLO LUIS MIGUEL» le llega al cliente como un regaño.
// Solo se arregla cuando TODO viene en mayúscula: si el prestamista lo escribió
// bien, no se le toca. Las palabras de enlace quedan en minúscula, que es como
// se escribe un nombre aquí: «Juan de la Cruz», no «Juan De La Cruz».
const ENLACES = ['de', 'del', 'la', 'las', 'los', 'y', 'da', 'do'];

export function nombreDeSaludo(nombre) {
  const n = String(nombre || '').trim();
  if (!n) return '';
  if (n !== n.toUpperCase()) return n;           // ya viene escrito a mano
  return n.toLowerCase().split(/\s+/).map((p, i) => (
    (i > 0 && ENLACES.includes(p)) ? p : p.charAt(0).toUpperCase() + p.slice(1)
  )).join(' ');
}

// El teléfono, en el formato que entiende wa.me. Colombia si vienen diez
// dígitos sueltos, que es como se anotan aquí.
export function aWhatsApp(telefono) {
  const d = String(telefono || '').replace(/\D/g, '');
  if (d.length === 10) return `57${d}`;
  if (d.length >= 11) return d;
  return null;
}

const CADA = {
  daily: 'diarios', weekly: 'semanales',
  biweekly: 'quincenales', monthly: 'mensuales',
};

// El estado de cuenta del cobradiario.
//
// Las cuatro cifras que él conoce, en el orden en que se las dijeron: cuánto
// le prestaron, cuánto tiene que devolver, de a cuánto, y cuánto lleva. Nada
// de capital ni de interés: eso ya se pactó y no se vuelve a discutir.
function mensajeCuotaFija(lineas, prestamo, estado, cierre) {
  const cada = CADA[prestamo.frequency] || 'por cuota';

  lineas.push('*Su cuenta al día de hoy*');
  lineas.push('');
  lineas.push(`Le presté: ${plata(prestamo.amount)}`);
  lineas.push(`Total a pagar: ${plata(estado.totalPactado)}`);
  if (estado.cuota > 0) {
    lineas.push(`Cuota: ${plata(estado.cuota)} ${cada}`);
  }
  lineas.push('');

  const llevaCuotas = estado.cuotasTotales > 0
    ? ` (${estado.cuotasPagadas} de ${estado.cuotasTotales} cuotas)`
    : '';
  lineas.push(`Ha abonado: ${plata(estado.abonado)}${llevaCuotas}`);

  if (estado.saldado) {
    lineas.push('');
    lineas.push('*Quedó a paz y salvo. Muchas gracias.*');
  } else {
    lineas.push(`*Le falta: ${plata(estado.deudaTotal)}*`);
    lineas.push('');
    const atrasadas = estado.cuotasAtrasadas || 0;
    if (atrasadas >= 2) {
      lineas.push(
        `Lleva ${atrasadas} cuotas atrasadas: ${plata(estado.atrasoEnPlata)}.`);
    } else if (atrasadas === 1) {
      lineas.push(`Le falta la cuota de hoy: ${plata(estado.atrasoEnPlata)}.`);
    } else {
      lineas.push('Va al día. Gracias por cumplir.');
    }
  }

  lineas.push('');
  lineas.push(cierre);
  return lineas.join('\n');
}

// El estado de cuenta de un corte concreto. Si no se dice cuál, el último.
// Si el cliente tiene más de un préstamo, hay que decirlo.
//
// El mensaje se arma por préstamo, y cada uno termina en «Debe hoy: $X». Con
// dos créditos abiertos, el cliente lee uno y cree que ésa es toda su deuda —
// y quien cobra queda como si le cobrara de más cuando llegue el segundo.
//
// No se juntan los dos en un solo mensaje a propósito: cada préstamo tiene su
// corte, su tasa y su historia, y mezclarlos haría un papel que no se puede
// revisar renglón por renglón. Se avisa que hay otro y se da el total.
//
// Vive aparte porque el mensaje tiene DOS finales —el préstamo con corte y el
// que todavía no lo tiene— y el aviso hace falta en los dos. Escrito dos veces,
// el día que cambie una palabra cambiaría en uno solo.

// LOS OTROS PRÉSTAMOS DEL CLIENTE Y EL TOTAL, en un solo sitio.
//
// Vive aquí y no en cada pantalla porque ya se olvidó una vez, y salió caro.
// Estados de cuenta armaba estos datos y se los pasaba al mensaje; Deuda del
// cliente —el botón «Mandarle su cuenta», que es el que se usa cuando el
// cliente llama— no los armaba. El mismo cliente recibía dos mensajes
// distintos según por dónde se le mandara: por un lado con su total y sus dos
// créditos nombrados, por el otro hablando de uno solo.
//
// Y lo peor: esa pantalla tiene el total escrito en letra grande justo encima
// del botón. Lo tenía a mano y no se lo pasaba al mensaje.
//
// Recibe la lista de préstamos ACTIVOS ya calculados —`{ prestamo, estado,
// pagos }`— y cuál de ellos es el del mensaje. El id se busca en `loan_id` o
// en `id`: las dos pantallas los traen de consultas distintas y los nombran
// distinto, y esa diferencia no tiene por qué llegar hasta aquí.
export function opcionesDeLosOtros(activos, principal, hoy = new Date()) {
  const lista = Array.isArray(activos) ? activos.filter(Boolean) : [];
  const idDe = (p) => (p && p.loan_id != null ? p.loan_id : (p || {}).id);
  const esteId = principal && principal.prestamo ? idDe(principal.prestamo) : null;

  return {
    otros: lista
      .filter(e => idDe(e.prestamo) !== esteId)
      .map(e => ({
        loan_id: idDe(e.prestamo),
        start_date: e.prestamo.start_date,
        deuda: (e.estado && e.estado.deudaTotal) || 0,
        corte: siguienteCorte(e.prestamo, hoy),
        // Para poder decir cuánto entregó el cliente ese día entre todos.
        pagos: e.pagos || [],
      })),
    // La suma de TODOS, éste incluido. Es la misma cifra que la tarjeta de
    // Deuda del cliente enseña en grande, y tiene que serlo: dos maneras de
    // sumarla son dos cifras que un día dejan de coincidir.
    totalCliente: lista.reduce(
      (t, e) => t + ((e.estado && e.estado.deudaTotal) || 0), 0),
  };
}

// Cuántos OTROS préstamos tiene el cliente, venga la lista con detalle o solo
// el conteo. Se pregunta en tres sitios y la respuesta tiene que ser la misma.
function cuantosOtros(opciones) {
  const lista = Array.isArray(opciones && opciones.otros) ? opciones.otros : [];
  return lista.length || Number((opciones || {}).otrosPrestamos) || 0;
}

// EL TOTAL VA ARRIBA, antes del detalle de este préstamo.
//
// El total ya se decía, pero en el penúltimo renglón, después de todo el
// desglose. Lo que queda en negrita a media pantalla es el «Debe hoy» de ESTE
// préstamo, y ése es el número que se lee y el que se repite por teléfono.
//
// Lenin repartió un millón entre los dos créditos del Dr. Suárez. La pantalla
// de Deuda decía $4.517.851 y el mensaje que iba a salir abría con $3.156.870.
// Ninguna de las dos cifras estaba mal —son el total y uno de los dos
// préstamos—, pero el papel no decía de cuál estaba hablando, y quien cobra no
// tiene cómo saber cuál de las dos creer.
//
// Solo cuando hay más de un préstamo: con uno solo, el total y el «debe hoy»
// son la misma cifra y repetirla arriba sobra.
function encabezadoDelTotal(lineas, opciones) {
  const otros = cuantosOtros(opciones);
  if (otros <= 0) return;
  const total = Number((opciones || {}).totalCliente);
  // Sin el total NO se inventa una cifra, igual que abajo: saber que hay otro
  // préstamo ya evita el malentendido.
  if (!Number.isFinite(total) || total <= 0) return;
  lineas.push(`*Entre sus ${otros + 1} préstamos debe hoy: ${plata(total)}*`);
  lineas.push('');
}

// «Debe hoy: $3.156.870» es la verdad cuando el cliente tiene UN préstamo. Con
// dos es la mitad de la verdad y se lee como el total. Se dice de cuál se está
// hablando; con uno solo la frase se queda como estaba, que es la que el
// cliente lleva meses recibiendo.
function debeHoy(opciones, cuanto) {
  return cuantosOtros(opciones) > 0
    ? `*De este préstamo debe hoy: ${plata(cuanto)}*`
    : `*Debe hoy: ${plata(cuanto)}*`;
}

// LO QUE ENTREGÓ ESE DÍA, entero.
//
// El Dr. Suárez mandó $1.000.000 el 19 de septiembre para sus dos créditos:
// $779.019 se fueron a uno y $220.981 al otro. Cada mensaje nombraba solo su
// parte, así que la cifra que él —y quien cobra— recordaba haber movido no
// aparecía en ningún papel. La cuenta «no daba» sin que nada estuviera mal
// calculado.
//
// Es el mismo agujero que tapa `loQueCancelo` para los préstamos que se
// cerraron con esa plata; éste cubre los que siguen vivos.
//
// SOLO LOS DÍAS QUE ESTE MENSAJE YA DETALLA, que es lo que recibe en
// `diasMostrados`. Recorriendo la vida entera del préstamo, uno de dos años
// arrastraría veinte bloques de éstos y el corte del mes —lo único que se está
// mandando hoy— quedaría enterrado.
//
// NO ES UNA CUENTA NUEVA: son los mismos abonos que ya salen arriba, sumados.
function loQueEntregoEseDia(lineas, opciones, pagos, diasMostrados) {
  const otros = Array.isArray(opciones && opciones.otros) ? opciones.otros : [];
  if (otros.length === 0) return;

  const dia = (f) => {
    const d = f instanceof Date ? f : new Date(f);
    return isNaN(d.getTime()) ? null : Math.floor(d.getTime() / 86400000);
  };
  const suma = (lista, cual) => (Array.isArray(lista) ? lista : [])
    .filter(p => dia(p.payment_date) === cual)
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);

  const dias = [...new Set((Array.isArray(pagos) ? pagos : [])
    .map(p => dia(p.payment_date))
    .filter(d => d !== null)
    .filter(d => !(diasMostrados instanceof Set) || diasMostrados.has(d)))]
    .sort((a, b) => a - b);

  for (const d of dias) {
    const repartos = otros
      .map(o => ({ o, cuanto: suma(o.pagos, d) }))
      .filter(x => x.cuanto > 0);
    // Ese día solo tocó este préstamo: no hay nada que aclarar.
    if (repartos.length === 0) continue;

    const mio = suma(pagos, d);
    const entregado = mio + repartos.reduce((s, x) => s + x.cuanto, 0);
    lineas.push('');
    lineas.push(`*El ${fechaLarga(new Date(d * 86400000))} entregó ${plata(entregado)}*`);
    lineas.push(`A este préstamo: ${plata(mio)}`);
    for (const { o, cuanto } of repartos) {
      const cual = o.start_date ? `Al del ${fechaLarga(o.start_date)}` : 'Al otro préstamo';
      lineas.push(`${cual}: ${plata(cuanto)}`);
    }
  }
}

function avisarDeLosOtros(lineas, opciones, pagos, diasMostrados) {
  const lista = Array.isArray(opciones && opciones.otros) ? opciones.otros : [];
  const cuantos = cuantosOtros(opciones);
  if (cuantos <= 0) return;
  const total = Number(opciones.totalCliente);

  loQueEntregoEseDia(lineas, opciones, pagos, diasMostrados);

  lineas.push('');

  if (lista.length > 0) {
    // NOMBRADOS UNO POR UNO, no contados.
    //
    // «Éste es uno de sus 2 préstamos» avisa de que hay otro pero no dice
    // cuál: con dos créditos abiertos el cliente no sabe si el que le falta es
    // el de tres millones o el de dos, y vuelve a llamar para preguntarlo —que
    // es la llamada que este mensaje venía a evitar—.
    //
    // Con la fecha en que se prestó ya lo reconoce: es como los tiene
    // apuntados él, y como los nombra por teléfono («el de julio»).
    lineas.push(lista.length === 1 ? '*Su otro préstamo*' : '*Sus otros préstamos*');
    for (const o of lista) {
      const cuando = o.start_date ? `Del ${fechaLarga(o.start_date)}` : 'Otro préstamo';
      // El corte va porque sin él este renglón parece una deuda que se puede
      // pagar hoy y ya: al cliente hay que decirle cuándo le vuelve a correr.
      const corte = o.corte ? ` — corta el ${fechaLarga(o.corte)}` : '';
      lineas.push(`${cuando}: debe ${plata(o.deuda)}${corte}`);
    }
  } else {
    // Sin el detalle, al menos se avisa de que existen. Es lo que se puede
    // decir con verdad desde donde no llega la lista.
    lineas.push(`_Éste es uno de sus ${cuantos + 1} préstamos._`);
  }

  // Sin el total NO se inventa una cifra: saber que hay otro préstamo ya evita
  // el malentendido, y un número puesto a la ligera lo empeoraría.
  if (Number.isFinite(total) && total > 0) {
    lineas.push(`*Entre todos debe hoy: ${plata(total)}*`);
  }
}

// Los préstamos que se cancelaron EL MISMO DÍA, con la misma plata.
//
// Javier Suárez entregó $850.000 para sus tres créditos: dos chiquitos quedaron
// a paz y salvo y el resto bajó el grande. Su mensaje decía «Total: $630.000»,
// porque un préstamo cerrado sale de la lista y el mensaje solo habla del suyo.
//
// Es el mismo error de creer que un papel cuenta toda la historia, al revés:
// aquel dejaba al cliente creyendo que debía menos; éste lo deja creyendo que
// ENTREGÓ MENOS, y con un papel del prestamista que parece confirmarlo. El que
// llama es él, con razón: «yo le mandé 850 y aquí me dice 630».
//
// Solo los del MISMO DÍA que los abonos de este mensaje. Un préstamo que se
// canceló hace tres semanas no tiene nada que ver con esta plata, y nombrarlo
// aquí sería mezclar dos cuentas en el mismo papel.
function loQueCancelo(lineas, cancelados, diasDeLosAbonos, totalAbonado) {
  const dia = (f) => {
    const d = f instanceof Date ? f : new Date(f);
    return isNaN(d.getTime()) ? null : Math.floor(d.getTime() / 86400000);
  };
  const mismos = (Array.isArray(cancelados) ? cancelados : [])
    .filter(c => diasDeLosAbonos.has(dia(c.fecha)))
    .sort((a, b) => new Date(a.start_date || 0) - new Date(b.start_date || 0));
  if (mismos.length === 0) return;

  lineas.push(mismos.length === 1 ? '*Ese mismo día canceló*' : '*Ese mismo día canceló*');
  for (const c of mismos) {
    const cual = c.start_date ? `Préstamo del ${fechaLarga(c.start_date)}` : 'Otro préstamo';
    lineas.push(`${cual}: ${plata(c.pagado)} — a paz y salvo`);
  }
  // La suma que cierra la cuenta. Es el renglón que contesta «yo le mandé 850».
  const entregado = mismos.reduce((s, c) => s + (Number(c.pagado) || 0), 0) + totalAbonado;
  lineas.push(`*Entre todo entregó: ${plata(entregado)}*`);
  lineas.push('');
}

// A dónde fue CADA abono, uno por uno.
//
// El mensaje decía cuánto entró cada día y, aparte y más abajo, cuánto se fue
// al interés y cuánto a la deuda. Con dos abonos el cliente no puede cuadrar su
// tarjeta: ve dos cifras arriba, dos abajo, y no sabe cuál va con cuál.
//
// NO ES UNA CUENTA NUEVA. Es la misma regla del motor —primero se cubre el
// interés debido, lo que sobra baja la deuda— aplicada en orden de fecha. Los
// totales tienen que dar exactamente `aInteres` y `aCapital`, y hay una prueba
// que lo comprueba: si algún día dejaran de cuadrar sería que aquí se escribió
// una segunda versión de la cuenta, que es como las dos se separan.
//
// El orden de fecha importa y no es un adorno: el primer abono del mes es el
// que paga el interés, y por eso el que abona dos veces ve que el segundo sí le
// bajó la deuda. Al revés le saldría lo contrario.
function repartirUnoAUno(abonos, debido) {
  let falta = Math.max(0, debido);
  return abonos.map(a => {
    const aInteres = Math.min(a.monto, falta);
    falta -= aInteres;
    return { ...a, aInteres, aCapital: a.monto - aInteres };
  });
}

// «20 ago 2026: $500.000 — $275.870 al interés, $224.130 a la deuda»
//
// Cuando el abono se va entero para un lado se dice con palabras y sin repetir
// la cifra: repetirla obliga a comparar dos números iguales para descubrir que
// son el mismo. Solo se desglosa el abono que de verdad se parte en dos.
function renglonDeAbono(a) {
  const base = `${fechaLarga(a.fecha)}: ${plata(a.monto)}`;
  if (a.aInteres <= 0) return `${base} — a la deuda`;
  if (a.aCapital <= 0) return `${base} — al interés`;
  return `${base} — ${plata(a.aInteres)} al interés, ${plata(a.aCapital)} a la deuda`;
}

export function mensajeDeEstado(cliente, prestamo, pagos, opciones = {}) {
  const saludo = opciones.saludo || 'Buenas';
  const cierre = opciones.cierre || 'Cualquier duda me escribe.';
  const hasta = opciones.hasta || new Date();

  const estado = estadoDelPrestamo(prestamo, pagos, hasta);
  const lineas = [];

  lineas.push(`${saludo}, ${nombreDeSaludo(cliente.name)}.`);
  lineas.push('');

  encabezadoDelTotal(lineas, opciones);

  // El cobradiario se explica con otras palabras.
  //
  // Al cliente de cuota fija no le sirve «capital» ni «interés pendiente»: a él
  // le dijeron el primer día cuánto tenía que devolver en total y cuánto dar
  // cada vez. Su pregunta es «¿cuántas llevo y cuánto me falta?», y eso es lo
  // que tiene que contestar el mensaje. Hablarle de intereses lo confunde y
  // además suena a que la cuenta cambió, cuando no cambió nada.
  if (estado.modalidad === 'cuota_fija') {
    return mensajeCuotaFija(lineas, prestamo, estado, cierre);
  }

  const liqs = estado.liquidaciones || [];
  const liq = liqs.length ? liqs[liqs.length - 1] : null;

  // Todavía no le ha cerrado ningún mes, pero ya viene abonando. Antes aquí se
  // mandaba un resumen pelado que no decía ni el interés que lleva corrido ni a
  // dónde va lo que entregó, y ése es justo el cliente que llama a preguntar:
  // acaba de empezar y no sabe cómo funciona. Se le enseña el corte que viene.
  if (!liq && estado.proyeccion && estado.proyeccion.acumulado > 0) {
    return conCorte(lineas, prestamo, estado, estado.proyeccion, cierre,
                    hasta, { proyectado: true,
                             // Este final también necesita el aviso, y se
                             // quedó sin él la primera vez. Es justo el del
                             // préstamo RECIÉN abierto —el segundo crédito de
                             // quien ya tenía uno—, que es el caso en que más
                             // falta hace decir que hay otro.
                             otrosPrestamos: opciones.otrosPrestamos,
                             otros: opciones.otros,
                             cancelados: opciones.cancelados,
                             totalCliente: opciones.totalCliente }, pagos);
  }

  // ── EL PRÉSTAMO QUE NO LIQUIDA POR CORTES ────────────────────────────────
  //
  // A Densy Fdez. le llegó el estado de cuenta entero en tres renglones:
  // «Capital: $3.284.800 / Debe hoy: $3.284.800», las dos cifras iguales y
  // nada más. Llevaba siete abonos desde mayo y ninguno salía.
  //
  // Su préstamo está marcado `inmediato`: no se liquida en cortes mensuales,
  // sino que la etiqueta de cada abono manda —lo anotado como interés cuenta
  // como interés—. Ese motor no produce liquidaciones, y el mensaje SOLO sabía
  // desglosar a partir de ellas. Los 21 préstamos así mandaban el resumen
  // pelado.
  //
  // Aquí no hay cortes que enseñar y no se van a inventar. Lo que sí hay son
  // los abonos con su etiqueta, que es exactamente de donde el motor saca sus
  // cifras: se dicen uno por uno y los totales tienen que dar los del motor.
  // Si algún día dejaran de dar, sería que aquí se escribió una segunda
  // versión de la cuenta.
  if (!liq && (pagos || []).length > 0) {
    const tasa = Number(prestamo.interest_rate) || 0;

    lineas.push(`*Su cuenta al ${fechaLarga(hasta)}*`);
    lineas.push('');
    if (prestamo.start_date) {
      lineas.push(`Préstamo del ${fechaLarga(prestamo.start_date)}: ` +
                  `${plata(prestamo.amount)}${tasa ? ` al ${tasa}%` : ''}`);
      lineas.push('');
    }

    // Del más viejo al más nuevo, como se van tachando en una tarjeta. El
    // servidor los entrega al revés —así se leen bien en la pantalla— y sin
    // ordenar aquí le llegan de atrás para adelante.
    const enOrden = [...pagos]
      .sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date));
    const uno = enOrden.length === 1;

    lineas.push(uno ? '*Su abono*' : '*Sus abonos*');
    for (const p of enOrden) {
      // La etiqueta del abono, que en este préstamo es la que manda. No se
      // recalcula nada: se dice a dónde fue según como se anotó.
      lineas.push(`${fechaLarga(p.payment_date)}: ${plata(p.amount)} — ` +
                  `${p.tipo === 'capital' ? 'a la deuda' : 'al interés'}`);
    }
    if (!uno) {
      const total = enOrden.reduce((s, p) => s + (Number(p.amount) || 0), 0);
      lineas.push(`Total: ${plata(total)}`);
      lineas.push(`Al interés: ${plata(estado.interesPagado)}`);
      lineas.push(`A la deuda: ${plata(estado.abonadoACapital)}`);
    }
    lineas.push('');

    // La resta que el cliente comprueba con el dedo: de cuánto arrancó, cuánto
    // le ha bajado, en cuánto va. Sin estos tres renglones «Capital» a secas es
    // una cifra suelta que no se puede cuadrar con nada.
    lineas.push('*Su deuda*');
    lineas.push(`Al empezar: ${plata(prestamo.amount)}`);
    if (estado.abonadoACapital > 0) {
      lineas.push(`Abonó a la deuda: ${plata(estado.abonadoACapital)}`);
    }
    lineas.push(`Queda en: ${plata(estado.capitalPendiente)}`);
    lineas.push('');

    // Y cómo va el interés. Las tres situaciones se dicen distinto porque son
    // distintas: deber interés, estar al día, e ir adelantado.
    if (estado.interesPendiente > 0) {
      lineas.push(`Interés pendiente: ${plata(estado.interesPendiente)}`);
    } else if (estado.aFavor > 0) {
      lineas.push(`Va adelantado en el interés: ${plata(estado.aFavor)}`);
    } else {
      // Es la frase que faltaba. Sin ella, capital y «debe hoy» son la misma
      // cifra y no hay nada que explique por qué — que es exactamente la
      // pregunta que llegó.
      lineas.push('El interés está al día.');
    }
    lineas.push('');
    lineas.push(debeHoy(opciones, estado.deudaTotal));

    avisarDeLosOtros(lineas, opciones, pagos);
    lineas.push('');
    lineas.push(cierre);
    return lineas.join('\n');
  }

  // EL PRÉSTAMO RECIÉN ABIERTO, todavía sin corte y sin un peso abonado.
  //
  // Aquí no hay nada que desglosar —no ha pasado nada— pero el mensaje se
  // quedaba en tres renglones: «Capital: $3.284.800 / Debe hoy: $3.284.800», y
  // las dos cifras iguales. Al cliente que venía recibiendo el estado de cuenta
  // completo eso se le lee como que el programa se dañó, y a quien lo manda le
  // toca explicar por teléfono lo que el papel tenía que decir.
  //
  // Lo que sí se puede decir con verdad es de cuándo viene el préstamo, de
  // cuánto, a qué tasa, cuándo le corre el próximo interés y de cuánto va a
  // ser. Nada de eso es una cuenta nueva: son los datos del préstamo y la
  // misma multiplicación que hará el corte.
  if (!liq) {
    const tasa = Number(prestamo.interest_rate) || 0;

    lineas.push('*Su cuenta al día de hoy*');
    lineas.push('');

    // De dónde viene, igual que en el mensaje con corte y con las mismas
    // palabras: el cliente tiene que reconocer el mismo papel de siempre.
    if (prestamo.start_date) {
      lineas.push(`Préstamo del ${fechaLarga(prestamo.start_date)}: ` +
                  `${plata(prestamo.amount)}${tasa ? ` al ${tasa}%` : ''}`);
      lineas.push('');
    }

    lineas.push(`Capital: ${plata(estado.capitalPendiente)}`);
    if (estado.interesPendiente > 0) {
      lineas.push(`Interés pendiente: ${plata(estado.interesPendiente)}`);
    }
    if (estado.aFavor > 0) {
      lineas.push(`Va adelantado: ${plata(estado.aFavor)}`);
    }
    lineas.push('');
    lineas.push(`*Debe hoy: ${plata(estado.deudaTotal)}*`);

    // AÚN NO LE HA CORRIDO INTERÉS, dicho con todas las letras.
    //
    // Es la única explicación de por qué el capital y el «debe hoy» son la
    // misma cifra. Sin ella, las dos líneas iguales parecen un error de la
    // máquina — y es exactamente lo que preguntan.
    const proximo = opciones.proximoCorte;
    if (proximo) {
      lineas.push('');
      const cuanto = Math.round(estado.capitalPendiente * (tasa / 100));
      lineas.push(
        `Todavía no le ha corrido interés. El ${fechaLarga(proximo)} ` +
        (cuanto > 0
          ? `se le suman ${plata(cuanto)} de interés.`
          : 'es su próximo corte.'));
    }

    avisarDeLosOtros(lineas, opciones);
    lineas.push('');
    lineas.push(cierre);
    return lineas.join('\n');
  }

  return conCorte(lineas, prestamo, estado, liq, cierre, hasta,
                  { proximoCorte: opciones.proximoCorte,
                    // Sin estas dos, el aviso de «tiene otro préstamo» no
                    // llegaba al final CON corte, que es por donde sale la
                    // inmensa mayoría de los mensajes.
                    otrosPrestamos: opciones.otrosPrestamos,
                    otros: opciones.otros,
                    cancelados: opciones.cancelados,
                    totalCliente: opciones.totalCliente }, pagos);
}

// El cuerpo del estado de cuenta, que se lee como la tarjeta de cobros de toda
// la vida: desde cuándo, cuánto debía, qué entregó y en qué día, cómo se
// reparte y con cuánto queda. Cada cifra con su fecha al lado —una cantidad
// suelta no la reconoce nadie, la fecha sí— y el total al final, que es lo que
// se busca con el dedo.
//
// Sirve igual para un corte que ya pasó que para el que viene. Lo único que
// cambia son los tiempos de los verbos: «debía» y «quedó» cuando ya ocurrió,
// «debe» y «quedará» cuando todavía no. Al que lleva un mes empezado hay que
// poder decirle a dónde va lo que ha entregado, que es lo que pregunta.
function conCorte(lineas, prestamo, estado, liq, cierre, hasta, opciones = {}, pagos = []) {
  const proyectado = !!opciones.proyectado;
  const tasa = Number(prestamo.interest_rate) || 0;

  // Lo que debía EL DÍA DEL CORTE, antes de aplicarle nada. Es el punto de
  // partida del renglón siguiente: de aquí se resta lo que entregó.
  //
  // Antes se usaba saldoFinal + lo que quedó debiendo de interés, que es el
  // saldo YA con los abonos descontados: al que había abonado en el mes le
  // decía «debía 9.000.000» cuando debía 11.000.000, y la resta no daba.
  const debidoAlCorte = liq.saldoInicial + liq.interesDelMes + liq.arrastreAnterior;

  lineas.push(`*Su cuenta al ${fechaLarga(hasta)}*`);
  lineas.push('');

  // De cuándo viene el préstamo.
  //
  // Sin esto, el renglón «interés que traía de antes: $17.250.000» es una cifra
  // caída del cielo: el cliente no sabe de cuánto tiempo se está hablando y no
  // puede comprobar nada. A Aldemar Cantillo le salían veintiséis meses de
  // interés acumulado sin que el mensaje dijera en ningún lado que el préstamo
  // es de mayo de 2024.
  //
  // Va arriba, antes de las cifras: es el punto de partida de toda la cuenta y
  // lo primero que busca quien quiere cuadrarla con su tarjeta.
  if (prestamo.start_date) {
    lineas.push(`Préstamo del ${fechaLarga(prestamo.start_date)}: ` +
                `${plata(prestamo.amount)}${tasa ? ` al ${tasa}%` : ''}`);
    lineas.push('');
  }

  // El salto que nadie podía seguir: «Préstamo del 13 jun: $4.000.000» y dos
  // renglones más abajo «Capital al 13 jul: $2.430.000». En medio hay cinco
  // abonos que el mensaje no nombraba, porque solo enseña los del mes en curso.
  //
  // Al cliente que lleva meses pagando eso se le lee como que sus primeros
  // abonos se perdieron, y es la clase de duda que termina en una discusión con
  // la tarjeta de papel encima de la mesa. Y no basta con decir cuánto abonó:
  // 4.000.000 menos 1.850.000 no da 2.430.000, porque $280.000 se fueron al
  // interés. Hay que decir el reparto para que la resta cuadre a mano.
  //
  // Es el mismo renglón que ahora sale en la pantalla de Deuda, con las mismas
  // palabras a propósito: el cliente ve una cosa y le llega la misma.
  const todasLasLiqs = estado.liquidaciones || [];
  // En la proyección `liq` no está en la lista —es el mes que aún no cierra—,
  // así que todos los cortes son anteriores. Sin esto, indexOf devuelve -1 y el
  // slice se comería el último mes en silencio.
  const iLiq = todasLasLiqs.indexOf(liq);
  const previas = iLiq === -1 ? todasLasLiqs : todasLasLiqs.slice(0, iLiq);
  const abonadoEnPrevias = previas.reduce((s, l) => s + l.acumulado, 0);
  if (abonadoEnPrevias > 0) {
    const aInteresAntes = previas.reduce((s, l) => s + l.aInteres, 0);
    const aCapitalAntes = previas.reduce((s, l) => s + l.aCapital, 0);
    // Del más viejo al más nuevo, como se leen en una tarjeta de papel.
    //
    // No basta con el orden en que vienen: el servidor los entrega del más
    // reciente al más antiguo —así se ven bien en la pantalla— y el motor los
    // conserva en ese orden dentro de cada corte. Sin ordenar aquí, al cliente
    // le llegaba «18 jul, 11 jul, 4 jul, 27 jun, 20 jun», que es justo al revés
    // de como va a ir tachándolos en su tarjeta.
    //
    // Cada corte reparte los SUYOS contra el interés que se debía ESE mes: un
    // abono de julio no puede pagar el interés de agosto. Por eso se reparte
    // dentro de cada corte y solo después se juntan todos en una lista.
    const sueltos = previas
      .flatMap(l => repartirUnoAUno(
        [...(l.abonos || [])].sort((a, b) => new Date(a.fecha) - new Date(b.fecha)),
        l.interesDelMes + l.arrastreAnterior))
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

    lineas.push(`*Antes del ${fechaLarga(liq.desde)}*`);

    // Se nombran uno por uno mientras quepan. Decir solo el total deja al
    // cliente sin nada que comparar con su tarjeta, que es exactamente lo que
    // vino a hacer con este mensaje.
    //
    // El tope existe porque hay carteras de años: un cliente que abona cada
    // semana desde 2024 son doscientas líneas y el mensaje deja de leerse. Al
    // pasarse, se resume por meses — y solo los meses en que sí abonó, que es
    // lo que vuelve corta la lista del que pagó dos veces en dos años.
    const CABEN = 12;
    if (sueltos.length <= CABEN) {
      for (const a of sueltos) lineas.push(renglonDeAbono(a));
    } else {
      // Resumido por meses, el reparto se dice igual: es la columna que el
      // cliente busca para entender por qué la deuda no bajó lo que abonó.
      for (const l of previas) {
        if (l.acumulado > 0) {
          lineas.push(renglonDeAbono({ fecha: l.corte, monto: l.acumulado,
                                       aInteres: l.aInteres, aCapital: l.aCapital })
            .replace(/^/, 'Mes del '));
        }
      }
    }

    lineas.push(`Total: ${plata(abonadoEnPrevias)}`);
    lineas.push(`Al interés: ${plata(aInteresAntes)}`);
    lineas.push(`A la deuda: ${plata(aCapitalAntes)}`);
    lineas.push('');
  }

  // OJO con la palabra. Esto es el CAPITAL al empezar el mes, no la deuda.
  //
  // Decía «Al 24 jun 2026 debía: $25.000.000» y ese día Aldemar debía
  // $42.250.000 — el capital más veintitrés meses de interés atrasado. La cifra
  // era la correcta para lo que quería decir; la palabra «debía» la convertía
  // en mentira, y encima en una que juega en contra del prestamista: sugiere
  // una deuda mucho menor de la real.
  lineas.push(`Capital al ${fechaLarga(liq.desde)}: ${plata(liq.saldoInicial)}`);

  // El orden es el de la suma: capital, lo que traía atrasado, lo del mes, y
  // el total. Leyendo hacia abajo cada renglón se apila sobre el anterior y la
  // última línea es la cuenta hecha. Al revés hay que ir y volver con el dedo.
  if (liq.arrastreAnterior > 0) {
    // Cuántos meses son, si se pueden contar. Una cifra grande sin su plazo al
    // lado suena a invento; con «(23 meses)» se puede dividir y comprobar.
    const porMes = liq.interesDelMes;
    const meses = porMes > 0 ? Math.round(liq.arrastreAnterior / porMes) : 0;
    lineas.push(`Interés atrasado a esa fecha: ${plata(liq.arrastreAnterior)}` +
                (meses > 1 ? ` (${meses} meses)` : ''));
  }

  // «del 10 jul al 10 ago 2026»: el año una sola vez si las dos fechas son del
  // mismo año. En el teléfono cada palabra de más parte el renglón.
  const desdeCorto = new Date(liq.desde).getUTCFullYear() === new Date(liq.corte).getUTCFullYear()
    ? fechaLarga(liq.desde).replace(/ \d{4}$/, '')
    : fechaLarga(liq.desde);
  lineas.push(`Interés del ${desdeCorto} al ${fechaLarga(liq.corte)}` +
              `${tasa ? ` (${tasa}%)` : ''}: ${plata(liq.interesDelMes)}`);
  lineas.push(`*${proyectado ? 'Debe' : 'Debía'} el ${fechaLarga(liq.corte)}: ` +
              `${plata(debidoAlCorte)}*`);
  lineas.push('');

  // Todos sus abonos juntos. En un corte que ya pasó se suman los de después,
  // porque para el cliente son lo mismo: plata que entregó. Separarlos por el
  // corte —cosa nuestra, no suya— es lo que le hace creer que no se la
  // anotaron. En el proyectado no hay nada «después»: ya vienen todos.
  const enOrden = (proyectado
    ? [...liq.abonos]
    : [...liq.abonos, ...(estado.abonosEnEspera || [])])
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  const totalAbonado = enOrden.reduce((t, a) => t + a.monto, 0);

  // Cómo se reparte: primero el interés, lo que sobra baja la deuda. Es el
  // orden de siempre y el que hay que poder explicar por teléfono.
  const espera = proyectado ? 0 : (estado.enEspera || 0);
  const aInteres = liq.aInteres + Math.min(espera, liq.quedaDebiendoInteres);
  const aCapital = liq.aCapital + Math.max(0, espera - liq.quedaDebiendoInteres);

  if (enOrden.length > 0) {
    // El mismo reparto de arriba, repartido abono por abono. Se cubre el
    // interés que se debía al corte —el del mes más lo que traía atrasado— y
    // lo que sobra baja la deuda; los totales dan lo mismo, por construcción.
    const conDestino = repartirUnoAUno(enOrden, liq.interesDelMes + liq.arrastreAnterior);

    // Singular y plural en todas las frases que hablan de los abonos. «Su
    // abono» seguido de «se reparten» se lee como escrito por una máquina, y a
    // un papel de plata que se lee así se le cree menos.
    const uno = enOrden.length === 1;
    lineas.push(uno ? '*Su abono*' : '*Sus abonos*');
    if (proyectado) {
      lineas.push(`_Se ${uno ? 'reparte' : 'reparten'} en el corte del ${fechaLarga(liq.corte)}._`);
    }
    for (const a of conDestino) lineas.push(renglonDeAbono(a));
    if (enOrden.length > 1) {
      lineas.push(`Total: ${plata(totalAbonado)}`);
      // Con un solo abono estos dos renglones repetirían el de arriba palabra
      // por palabra. Con varios son la suma de la columna, que es lo que se
      // compara contra la tarjeta.
      lineas.push(`Al interés: ${plata(aInteres)}`);
      lineas.push(`A la deuda: ${plata(aCapital)}`);
    }
    lineas.push('');

    const debeAunInteres = Math.max(0, liq.quedaDebiendoInteres - espera);
    if (debeAunInteres > 0) {
      lineas.push(
        `${proyectado ? 'Queda' : 'Quedó'} debiendo ${plata(debeAunInteres)} ` +
        `del interés, que se suma al próximo mes.`);
      lineas.push('');
    }
  } else {
    // Ojo con esta frase: habla de ESTE corte, no de toda la vida del préstamo.
    //
    // Decía «No se ha registrado ningún abono» a secas, y a un cliente que
    // abonó hace dos años le llegaba que nunca ha pagado nada. Le pasó a
    // Aldemar Cantillo: dos abonos de $750.000 en 2024, bien registrados, y el
    // mensaje se los negaba. Con la tarjeta en la mano, el que queda mal es
    // quien manda el mensaje.
    //
    // La cifra siempre estuvo bien; era la frase la que mentía.
    const abonadoAntes = (pagos || []).reduce((t, x) => t + (Number(x.amount) || 0), 0);
    if (abonadoAntes > 0) {
      lineas.push('*En este mes no ha abonado.*');
      lineas.push(`Lleva ${plata(abonadoAntes)} abonados desde que empezó.`);
    } else {
      lineas.push('*No se ha registrado ningún abono.*');
    }
    lineas.push('');
  }

  // EN CUÁNTO QUEDÓ LA DEUDA. La pregunta de verdad del cliente que abona.
  //
  // El mensaje enseñaba el capital al empezar el mes, arriba del todo, y el
  // «debe hoy» al final —con siete renglones de interés en medio—. Quien abonó
  // $784.130 a la deuda no tenía dónde ver que su deuda bajó justo eso: le
  // tocaba restar dos cifras separadas por media pantalla y confiar en que el
  // resto de renglones no le tocaban el capital.
  //
  // Los tres renglones son una resta que se comprueba con el dedo. Y va
  // SIEMPRE, aunque no haya abonado a la deuda: al que solo alcanzó a pagar el
  // interés hay que decirle con todas las letras que su deuda quedó igual. Ésa
  // es la llamada que entra al mes siguiente.
  const capitalDespues = Math.max(0, liq.saldoInicial - aCapital);
  lineas.push('*Su deuda*');
  lineas.push(`Al ${fechaLarga(liq.desde)}: ${plata(liq.saldoInicial)}`);
  if (aCapital > 0) {
    lineas.push(`Abonó a la deuda: ${plata(aCapital)}`);
    lineas.push(`${proyectado ? 'Quedará' : 'Queda'} en: ${plata(capitalDespues)}`);
  } else if (totalAbonado > 0) {
    const uno = enOrden.length === 1;
    lineas.push(`${uno ? 'Su abono alcanzó' : 'Sus abonos alcanzaron'} para el interés, ` +
                `así que la deuda ${proyectado ? 'quedará' : 'queda'} igual: ` +
                plata(capitalDespues));
  } else {
    // Sin un peso abonado no se puede decir «alcanzaron para el interés»: no
    // alcanzó nada. Se dice la deuda y ya.
    lineas.push(`${proyectado ? 'Quedará' : 'Queda'} en: ${plata(capitalDespues)}`);
  }
  lineas.push('');

  // Va AQUÍ, entre la deuda y el «debe hoy»: el cliente lee cuánto le quedó de
  // este préstamo, después qué canceló con la misma plata, y la suma de todo lo
  // que entregó. Puesto al final, después del «debe hoy», parecería otra cosa.
  // Los días de los abonos que este mensaje acaba de detallar. Los usan las dos
  // aclaraciones de «lo que entregó»: la de los préstamos que canceló con esa
  // plata y la de los que siguen vivos. Es el MISMO conjunto a propósito — dos
  // formas de contarlo darían dos versiones del mismo día.
  const diasDeLosAbonos = new Set(
    enOrden.map(a => Math.floor(new Date(a.fecha).getTime() / 86400000)));

  loQueCancelo(lineas, opciones.cancelados, diasDeLosAbonos, totalAbonado);

  // Lo de arriba menos todo lo que entregó. Sale de restar lo que se acaba de
  // leer, y es el mismo número que enseña la pantalla de Deuda.
  const queda = Math.max(0, debidoAlCorte - totalAbonado);
  if (proyectado) {
    // Al mes le quedan cinco días o menos: ya se cobra completo, así que esta
    // cifra es la de hoy y no una promesa para el día del corte.
    if (estado.mesConsumido) {
      lineas.push(debeHoy(opciones, queda));
      lineas.push(`Corte: ${fechaLarga(liq.corte)}`);
    } else {
      lineas.push(`*Al ${fechaLarga(liq.corte)} queda debiendo: ${plata(queda)}*`);
      // Lo que tendría que entregar para quedar en cero HOY. Es menos, porque
      // al mes todavía le falta y su interés aún no se cobra.
      if (estado.deudaTotal < queda) {
        lineas.push(`Si paga todo hoy: ${plata(estado.deudaTotal)}`);
      }
    }
  } else {
    lineas.push(debeHoy(opciones, queda));
    const proximo = opciones.proximoCorte;
    if (proximo) lineas.push(`Próximo corte: ${fechaLarga(proximo)}`);
  }

  avisarDeLosOtros(lineas, opciones, pagos, diasDeLosAbonos);

  lineas.push('');
  lineas.push(cierre);
  return lineas.join('\n');
}

// El enlace que abre WhatsApp con el mensaje ya escrito. No manda nada solo:
// se abre la conversación y quien cobra le da a enviar. A propósito — un
// mensaje automático con una cifra equivocada no se puede recoger.
export function enlaceDeWhatsApp(telefono, mensaje) {
  const numero = aWhatsApp(telefono);
  if (!numero) return null;
  return `https://wa.me/${numero}?text=${encodeURIComponent(mensaje)}`;
}

// El recordatorio corto, el que se manda antes de que el cliente falle.
//
// No es el estado de cuenta: ése es la foto del corte, largo y con el
// desglose. Éste es el toque en el hombro —«hoy le vence»— y tiene que caber
// de un vistazo en la pantalla del teléfono. Un recordatorio largo no se lee.
//
// Cambia según en qué situación está, porque no es lo mismo avisarle al que
// todavía no debe nada que reclamarle al que lleva tres semanas.
export function mensajeDeAviso(cliente, cobro, opciones = {}) {
  const saludo = opciones.saludo || 'Buenas';
  const cierre = opciones.cierre || 'Cualquier duda me escribe.';
  const lineas = [`${saludo}, ${nombreDeSaludo(cliente.name)}.`, ''];

  const monto = plata(cobro.aCobrar);
  if (cobro.situacion === 'vence_manana') {
    lineas.push(`Le recuerdo que *mañana* le vence su cuota de ${monto}.`);
  } else if (cobro.situacion === 'vence_hoy') {
    lineas.push(`Le recuerdo que *hoy* le vence su cuota de ${monto}.`);
  } else if (cobro.situacion === 'atrasado') {
    // Al cobradiario se le habla en cuotas, que es como lleva él la cuenta.
    // «Le venció hace 2 días» no le dice nada a quien paga todos los días;
    // «lleva 3 cuotas» sí, porque es lo que tiene apuntado en su tarjeta.
    const atrasadas = cobro.cuotasAtrasadas || 0;
    if (atrasadas >= 2) {
      lineas.push(`Le recuerdo que lleva *${atrasadas} cuotas* atrasadas: ${monto}.`);
    } else {
      const dias = cobro.diasAtraso;
      lineas.push(
        `Le recuerdo que tiene pendiente ${monto}` +
        (dias > 0
          ? `, que le venció hace ${dias} ${dias === 1 ? 'día' : 'días'}.`
          : '.'));
    }
  } else {
    lineas.push(`Le recuerdo que tiene pendiente ${monto}.`);
  }

  lineas.push('');
  lineas.push(cierre);
  return lineas.join('\n');
}
